(() => {
  'use strict';
  if (window.vbCart?.isNativeDrawer) return;

  // Resolve fetch when the request starts so Shopify can observe cart mutations.
  const cartFetch = (...args) => window.fetch(...args);
  const route = () => window.Shopify?.routes?.root || '/';
  const current = () => document.querySelector('vb-cart-drawer[data-vb-cart]');
  const find = (scope, selector) => scope.querySelector(selector);
  const findAll = (scope, selector) => Array.from(scope.querySelectorAll(selector));
  const build = '20260914-instant-cart-1';

  class VbCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = find(this, 'dialog');
      this.status = find(this, '[data-vb-cart-status]');
      this.errorBox = find(this, '[data-vb-cart-error-box]');
      this.pending = 0;
      this.queue = Promise.resolve();
      this.stale = false;
      this.diagnose('connected');
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => this.onClick(event), { ...options, capture: true });
      this.addEventListener('change', (event) => this.onChange(event), { ...options, capture: true });
      this.dialog.addEventListener('close', () => this.onClose(), options);
      this.connectTriggers();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.onClose();
      this.controller?.abort();
      this.controller = null;
    }

    connectTriggers() {
      findAll(document, '[data-vb-cart-open], .vb-header #cart-icon-bubble').forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
      });
    }

    reveal(trigger) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        window.location.assign(route() + 'cart');
        return false;
      }
      if (!this.dialog.open) {
        this.returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
        this.dialog.showModal();
        document.documentElement.classList.add('vb-cart-lock');
      }
      return true;
    }

    open(trigger, payload) {
      if (!this.reveal(trigger)) return Promise.resolve(false);
      // Reopening an in-flight drawer must not enqueue another cart read.
      if (this.pending) return this.queue;
      // A just-confirmed cart can reopen without locking its controls again.
      // Checkout still validates the current Shopify cart before navigation.
      if (!payload && !this.stale && this.confirmedAt && Date.now() - this.confirmedAt < 15000) return Promise.resolve(true);
      return this.refresh(payload);
    }

    paintPending() {
      const pending = this.adding;
      if (!pending) return;
      const content = find(this, '[data-vb-cart-content]');
      find(content, '[data-vb-cart-pending-item]')?.remove();
      find(content, '.vb-cart-empty')?.remove();
      let list = find(content, '.vb-cart-items');
      if (!list) {
        list = document.createElement('ul');
        list.className = 'vb-cart-items';
        list.setAttribute('role', 'list');
        find(content, '.vb-cart-scroll').append(list);
      }
      list.append(pending.item.cloneNode(true));
      let footer = find(content, '.vb-cart-footer');
      if (!footer) {
        footer = find(this, '[data-vb-cart-preview-footer]').content.firstElementChild.cloneNode(true);
        content.append(footer);
      }
      const total = Number(content.dataset.totalPrice) + pending.unit;
      const amount = find(footer, '.vb-cart-total strong');
      if (amount && Number.isSafeInteger(total)) amount.textContent = new Intl.NumberFormat(
        document.documentElement.lang || 'fr', {style:'currency',currency:this.dataset.currency || 'EUR'}
      ).format(total / 100);
      this.status.textContent = this.dataset.addingText;
      this.setBusy(true);
    }

    add(choice, trigger, work) {
      if (this.adding) return Promise.resolve(false);
      const template = find(this, '[data-vb-cart-preview-variant="' + choice.dataset.vbVariant + '"]');
      if (!template?.content?.firstElementChild) return Promise.resolve(false);
      const started = performance.now();
      this.confirmedContent = find(this, '[data-vb-cart-content]').cloneNode(true);
      this.adding = {item:template.content.firstElementChild.cloneNode(true),unit:Number(choice.dataset.vbUnitPrice)};
      this.paintPending();
      this.reveal(trigger);
      this.dataset.vbCartFeedbackMs = String(Math.round((performance.now() - started) * 10) / 10);
      delete this.dataset.vbCartConfirmedMs;
      const context = this.sectionContext();
      return this.enqueue(async () => {
        try {
          const cart = await work({sections:[this.dataset.sectionId],sections_url:context.pathname + context.search});
          this.adding = null;
          // No second open: closing the drawer during the request stays respected.
          await this.renderFresh(cart);
          this.dataset.vbCartConfirmedMs = String(Math.round(performance.now() - started));
          return true;
        } catch (error) {
          this.adding = null;
          // Restore readable confirmed contents; a possibly committed add is never replayed.
          find(this, '[data-vb-cart-content]').replaceWith(this.confirmedContent.cloneNode(true));
          this.stale = true;
          try { await this.renderFresh(); } catch { this.stale = true; }
          this.showError(error.message);
          return false;
        } finally {
          this.adding = null;
          this.confirmedContent = null;
        }
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onClose() {
      if (!document.querySelector('vb-cart-drawer dialog[open]')) {
        document.documentElement.classList.remove('vb-cart-lock');
      }
      const trigger = this.returnFocus;
      this.returnFocus = null;
      if (trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
      else if (trigger) find(document, '.vb-header #cart-icon-bubble')?.focus({ preventScroll: true });
    }

    captureFocus() {
      const active = document.activeElement;
      const line = active?.closest('[data-vb-cart-line]');
      if (!line || !this.contains(line)) return null;
      const control = active.hasAttribute('data-vb-cart-quantity') ? '[data-vb-cart-quantity]'
        : active.hasAttribute('data-vb-cart-remove') ? '[data-vb-cart-remove]'
          : active.hasAttribute('data-vb-cart-adjust') ? `[data-vb-cart-adjust="${active.dataset.vbCartAdjust}"]`
            : '.vb-cart-item-title';
      return { key: line.dataset.lineKey, index: Number(line.dataset.lineIndex), control };
    }

    restoreFocus(saved) {
      if (!saved || !this.dialog.open) return;
      const lines = findAll(this, '[data-vb-cart-line]');
      const line = lines.find((item) => item.dataset.lineKey === saved.key)
        || lines[Math.min(saved.index, lines.length - 1)];
      let target = line && find(line, saved.control);
      if (!target || target.disabled) target = line && find(line, '.vb-cart-item-title');
      (target || find(this, '[data-vb-cart-title]')).focus({ preventScroll: true });
    }

    setBusy(busy) {
      const content = find(this, '[data-vb-cart-content]');
      this.dialog.setAttribute('aria-busy', String(busy));
      if (content) {
        content.inert = busy || this.stale;
        const checkout = find(content, '[data-vb-cart-checkout]');
        if (checkout) {
          if (busy || this.stale) checkout.setAttribute('aria-disabled', 'true');
          else checkout.removeAttribute('aria-disabled');
        }
      }
      find(this, '[data-vb-cart-retry]').disabled = busy;
    }

    showError(message) {
      this.status.textContent = '';
      find(this, '[data-vb-cart-error]').textContent = message || this.dataset.errorText;
      this.errorBox.hidden = false;
    }

    enqueue(work) {
      const savedFocus = this.captureFocus();
      this.pending += 1;
      this.setBusy(true);
      if (savedFocus && this.dialog.open) find(this, '[data-vb-cart-title]').focus({ preventScroll: true });
      const operation = this.queue.then(async () => {
        if (!this.isConnected) return false;
        this.diagnostic = {};
        this.diagnose('operation-start');
        this.errorBox.hidden = true;
        this.status.textContent = this.adding ? this.dataset.addingText : this.dataset.loadingText;
        try {
          const ok = await work();
          if (ok) this.status.textContent = this.dataset.updatedText;
          return ok;
        } catch (error) {
          this.reportFailure('operation-error', { failedStage: this.dataset.vbCartStage, errorName: error.name });
          this.stale = true;
          this.showError(error.message);
          return false;
        } finally {
          this.pending -= 1;
          this.setBusy(this.pending > 0);
          if (!this.pending) this.restoreFocus(savedFocus);
        }
      });
      this.queue = operation.catch(() => false);
      return this.queue;
    }

    async request(url, body, accept = 'application/json') {
      const requestUrl = new URL(url, window.location.href);
      const context = {
        method: body ? 'POST' : 'GET',
        accept,
        origin: requestUrl.origin,
        path: requestUrl.pathname,
        requestThemeId: requestUrl.searchParams.get('preview_theme_id'),
        renderedThemeId: this.dataset.previewThemeId || null
      };
      this.diagnose('request-start', context);
      let response;
      try {
        response = await cartFetch(url, {
          method: body ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: accept, ...(body ? { 'Content-Type': 'application/json' } : {}) },
          signal: this.controller.signal,
          ...(body ? { body: JSON.stringify(body) } : {})
        });
      } catch (error) {
        this.reportFailure('network', { ...context, errorName: error.name });
        throw new Error(this.dataset.errorText);
      }
      const finalUrl = response.url ? new URL(response.url) : requestUrl;
      this.lastResponse = {
        ...context,
        status: response.status,
        contentType: response.headers?.get('content-type'),
        redirected: Boolean(response.redirected),
        finalOrigin: finalUrl.origin,
        finalPath: finalUrl.pathname,
        finalSections: finalUrl.searchParams.get('sections'),
        finalThemeId: finalUrl.searchParams.get('preview_theme_id')
      };
      this.diagnose('response-received', this.lastResponse);
      let result;
      try { result = await response.json(); }
      catch {
        this.reportFailure('invalid-json', this.lastResponse);
        throw new Error(this.dataset.errorText);
      }
      if (!response.ok || result?.status >= 400) {
        this.reportFailure('http-error', this.lastResponse);
        throw new Error(typeof result?.description === 'string' ? result.description : this.dataset.errorText);
      }
      const jsonKeys = result !== null && typeof result === 'object' ? Object.keys(result) : [];
      this.diagnose('json-ready', {
        jsonType: result === null ? 'null' : Array.isArray(result) ? 'array' : typeof result,
        jsonArray: Array.isArray(result),
        jsonKeyCount: jsonKeys.length,
        jsonKeys: jsonKeys.slice(0, 16)
      });
      return result;
    }

    diagnose(stage, details = {}) {
      this.diagnostic = { ...this.diagnostic, ...details, build, stage, sectionId: this.dataset.sectionId };
      this.dataset.vbCartStage = stage;
      this.dataset.vbCartDiagnostic = JSON.stringify(this.diagnostic);
    }

    reportFailure(reason, context) {
      // Only routing metadata: never log cart contents, customer details or form data.
      this.diagnose(reason, { failedStage: this.dataset.vbCartStage, ...context });
      console.warn('[Viebys cart] ' + JSON.stringify(this.diagnostic));
    }

    sectionContext() {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      url.searchParams.delete('_vb_cart');
      // The preview bar can clean the address and inject its ID through patched
      // fetch. Our captured fetch needs the rendered unpublished theme explicitly.
      const previewId = this.dataset.previewThemeId || '';
      if (!url.searchParams.get('preview_theme_id') && /^\d+$/.test(previewId)) {
        url.searchParams.set('preview_theme_id', previewId);
      }
      return url;
    }

    render(html) {
      this.diagnose('render-input', {
        sectionType: html === null ? 'null' : typeof html,
        htmlLength: typeof html === 'string' ? html.length : 0
      });
      if (typeof html !== 'string' || !html.trim()) return false;
      this.diagnose('parse-html');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const root = findAll(parsed, 'vb-cart-drawer[data-vb-cart]')
        .find((element) => element.dataset.sectionId === this.dataset.sectionId);
      const content = root && find(root, '[data-vb-cart-content]');
      const count = Number(content?.dataset.itemCount);
      this.diagnose('parsed-html', { rootFound: Boolean(root), contentFound: Boolean(content), countValid: Number.isInteger(count) && count >= 0 });
      if (!content || !Number.isInteger(count) || count < 0 || !this.isConnected) return false;
      this.diagnose('replace-content');
      find(this, '[data-vb-cart-content]').replaceWith(document.importNode(content, true));
      this.stale = false;
      if (this.adding) {
        this.confirmedContent = find(this, '[data-vb-cart-content]').cloneNode(true);
        this.paintPending();
      }
      this.setBusy(this.pending > 0);
      findAll(document, '.vb-header .vb-cart-count, [data-vb-cart-count]').forEach((bubble) => {
        bubble.textContent = String(count);
      });
      findAll(document, '.vb-header #cart-icon-bubble').forEach((trigger) => {
        const label = find(trigger, '.vb-cart-label')?.textContent.trim() || find(this, '[data-vb-cart-title]').textContent.trim();
        trigger.setAttribute('aria-label', `${label} — ${count}`);
      });
      document.dispatchEvent(new CustomEvent('vb:cart-updated', { detail: { itemCount: count } }));
      this.diagnose('render-complete');
      this.confirmedAt = Date.now();
      return true;
    }

    async renderFresh(payload) {
      if (window.vbBundles?.enabled) {
        const known = Array.isArray(payload?.items) ? payload : undefined;
        const checked = await window.vbBundles.sync(known);
        if (!known || checked !== known) payload = undefined;
      }
      const sections = payload?.response?.sections || payload?.sections;
      if (this.render(sections?.[this.dataset.sectionId])) return true;
      const url = this.sectionContext();
      url.searchParams.set('sections', this.dataset.sectionId);
      url.searchParams.set('_vb_cart', String(Date.now()));
      // On a product URL, Accept: application/json selects the product JSON
      // endpoint before sections rendering. Match the browser's normal Accept.
      const result = await this.request(url.toString(), undefined, '*/*');
      if (!this.render(result?.[this.dataset.sectionId])) {
        this.reportFailure('section-unavailable', this.lastResponse);
        throw new Error(this.dataset.errorText);
      }
      return true;
    }

    refresh(payload) {
      if (this.pending) return this.queue;
      // A queued payload may predate another mutation; fetch the latest state instead.
      const usable = this.pending ? undefined : payload;
      return this.enqueue(() => this.renderFresh(usable));
    }

    async settleChange(cart) {
      const tasks = [];
      document.dispatchEvent(new CustomEvent('vb:cart-changed', {
        detail: {
          cart,
          // Register synchronously. The task must not await vbCart.refresh: this
          // operation already owns the queue and renders again after cleanup.
          waitUntil: (task) => tasks.push(Promise.resolve(task))
        }
      }));
      await Promise.all(tasks);
      return tasks.length > 0;
    }

    change(line, quantity) {
      if (this.pending || this.stale) return Promise.resolve(false);
      const input = find(line, '[data-vb-cart-quantity]');
      const minimum = Number(input.dataset.min) || 1;
      const step = Number(input.dataset.step) || 1;
      const maximum = input.hasAttribute('max') ? Number(input.max) : Infinity;
      const valid = Number.isSafeInteger(quantity) && quantity >= 0 && quantity <= maximum
        && (quantity === 0 ? line.dataset.canRemove !== 'false' : quantity >= minimum && (quantity - minimum) % step === 0);
      if (!valid) {
        input.value = input.dataset.currentQuantity;
        this.showError(this.dataset.invalidQuantityText);
        input.focus({ preventScroll: true });
        return Promise.resolve(false);
      }
      if (quantity === Number(input.dataset.currentQuantity)) return Promise.resolve(true);
      const context = this.sectionContext();
      const body = {
        id: line.dataset.lineKey,
        quantity,
        sections: [this.dataset.sectionId],
        sections_url: context.pathname + context.search
      };
      return this.enqueue(async () => {
        let result;
        try {
          if (window.vbBundles?.enabled && line.dataset.vbBundleRole === 'main') result = await window.vbBundles.change(line.dataset.vbBundleGroup, quantity);
          else result = await this.request(route() + 'cart/change.js', body);
        }
        catch (error) {
          // Network errors can follow a committed change. Read; never replay the mutation.
          try {
            await this.settleChange(null);
            await this.renderFresh();
          }
          catch { this.stale = true; }
          this.showError(error.message);
          return false;
        }
        if (window.vbBundles?.enabled) result = await window.vbBundles.sync(result);
        const cleaned = await this.settleChange(result);
        return this.renderFresh(cleaned ? undefined : result);
      });
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const target = event.target;
      if (target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        return;
      }
      if (target.closest('[data-vb-cart-close]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      } else if (target.closest('[data-vb-cart-retry]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.pending) {
          if (isCartPage() && cartPageLocks.size) refreshCartPage();
          else this.refresh();
        }
      } else if (target.closest('[data-vb-cart-remove], [data-vb-cart-adjust]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const control = target.closest('button');
        if (control.disabled || this.pending || this.stale) return;
        const line = control.closest('[data-vb-cart-line]');
        const input = find(line, '[data-vb-cart-quantity]');
        let quantity = 0;
        if (control.hasAttribute('data-vb-cart-adjust')) {
          quantity = Number(input.dataset.currentQuantity) + Number(control.dataset.vbCartAdjust) * (Number(input.dataset.step) || 1);
          if (quantity < Number(input.dataset.min)) quantity = line.dataset.canRemove === 'false' ? Number(input.dataset.min) : 0;
        }
        this.change(line, quantity);
      } else if (target.closest('[data-vb-cart-checkout]') && (this.pending || this.stale)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    onChange(event) {
      if (!event.target.matches('[data-vb-cart-quantity]')) return;
      event.stopImmediatePropagation();
      const input = event.target;
      if (input.disabled || this.pending || this.stale) return;
      this.change(input.closest('[data-vb-cart-line]'), input.value.trim() === '' ? NaN : Number(input.value));
    }
  }

  customElements.define('vb-cart-drawer', VbCartDrawer);
  window.vbCart = {
    isNativeDrawer: true,
    build,
    canPreview: choice => typeof current()?.dialog?.showModal === 'function' && Boolean(current()?.querySelector('[data-vb-cart-preview-variant="' + choice.dataset.vbVariant + '"]')),
    get sectionId() { return current()?.dataset.sectionId || 'vb-cart'; },
    add: (choice, trigger, work) => current()?.add(choice, trigger, work) || Promise.resolve(false),
    open: (trigger, payload) => current()?.open(trigger, payload) || Promise.resolve(false),
    refresh: (payload) => current()?.refresh(payload) || Promise.resolve(false),
    close: () => current()?.close()
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || event.defaultPrevented) return;
    const trigger = event.target.closest('[data-vb-cart-open], .vb-header #cart-icon-bubble');
    if (!trigger || !current() || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.vbCart.open(trigger);
  }, true);

  document.addEventListener('vb:cart-added', (event) => {
    const detail = event.detail || {};
    window.vbCart.open(detail.trigger, detail);
  });
  let cartPageRefresh = null;
  const cartPageLocks = new Map();
  const isCartPage = () => window.location.pathname.replace(/\/+$/, '') === new URL(route() + 'cart', window.location.origin).pathname.replace(/\/+$/, '');
  function refreshCartPage() {
    const drawer = current();
    if (!drawer || !isCartPage()) return Promise.resolve(false);
    if (cartPageRefresh) return cartPageRefresh;
    const targets = ['main-cart-items','main-cart-footer'].map(id => document.getElementById(id));
    targets.filter(Boolean).forEach(target => {
      if (!cartPageLocks.has(target)) cartPageLocks.set(target,target.inert);
      target.inert = true;
    });
    cartPageRefresh = drawer.enqueue(async () => {
      // This also verifies the gifts before either cart surface becomes usable.
      await drawer.renderFresh();
      try {
        if (targets.some(target => !target?.dataset.id || !target.isConnected)) throw new Error(drawer.dataset.errorText);
        const url = drawer.sectionContext();
        url.searchParams.set('sections',targets.map(target => target.dataset.id).join(','));
        url.searchParams.set('_vb_cart',String(Date.now()));
        const sections = await drawer.request(url.toString(),undefined,'*/*');
        // Validate both responses before replacing either zone. Keep the cart-items
        // custom element and the footer container, including their subscriptions.
        const replacements = targets.map(target => {
          const html = sections?.[target.dataset.id];
          if (typeof html !== 'string' || !html.trim()) throw new Error(drawer.dataset.errorText);
          const parsed = new DOMParser().parseFromString(html,'text/html');
          const fresh = parsed.getElementById(target.id);
          const previousContents = fresh && findAll(target,'.js-contents');
          const nextContents = fresh && findAll(fresh,'.js-contents');
          if (!fresh || !previousContents.length || previousContents.length !== nextContents.length || !target.isConnected) throw new Error(drawer.dataset.errorText);
          const owner = target.id === 'main-cart-items' ? target.closest('cart-items') : target;
          const nextOwner = target.id === 'main-cart-items' ? fresh.closest('cart-items') : fresh;
          const buttons = findAll(target,'[name="checkout"]'), nextButtons = findAll(fresh,'[name="checkout"]');
          if (!owner || !nextOwner || buttons.length !== nextButtons.length) throw new Error(drawer.dataset.errorText);
          return {owner,nextOwner,previousContents,nextContents,buttons,nextButtons};
        });
        replacements.forEach(({owner,nextOwner,previousContents,nextContents,buttons,nextButtons}) => {
          previousContents.forEach((content,index) => {content.innerHTML = nextContents[index].innerHTML;});
          owner.className = nextOwner.className;
          buttons.forEach((button,index) => {button.disabled = nextButtons[index].disabled;});
        });
        cartPageLocks.forEach((inert,target) => {if (target.isConnected) target.inert = inert;});
        cartPageLocks.clear();
        return true;
      } catch (error) {
        // A failed classic-page request must not invalidate the fresh drawer.
        drawer.showError(error.message);
        return false;
      }
    }).then(ok => {
      if (!ok && drawer.dialog && !drawer.dialog.open) {
        drawer.dialog.showModal();
        document.documentElement.classList.add('vb-cart-lock');
      }
      return ok;
    }).finally(() => {cartPageRefresh = null;});
    return cartPageRefresh;
  }
  window.addEventListener('pageshow', () => {
    // Browser history can restore stale HTML without setting event.persisted.
    if (isCartPage()) refreshCartPage();
    else if (!current()?.pending) window.vbCart.refresh();
  });
  document.addEventListener('shopify:section:load', () => current()?.connectTriggers());
  document.addEventListener('shopify:section:select', (event) => {
    if (event.detail?.sectionId === current()?.dataset.sectionId) window.vbCart.open();
  });
})();
